import { ArrowRight, Clock, Briefcase, DollarSign, CheckCircle2, Menu } from "lucide-react";
import AppNavbar from "../AppNarbar";
import Sidebar from "../Sidebar";

/* The contractor dashboard as a visitor sees it before they have one: the same
   navbar, sidebar, and card furniture as the real thing, filled with static
   placeholder data. No auth, no API calls, no writes. Rendered inert by the
   caller (pointer-events: none).

   Twin of CompanyDashboardPreview, and here for the same reason: two flows
   show it (the get-started teaser and the need-first signup's reveal), so it
   can't live inside either page without making them circular. */

const SERIF = "'Bodoni Moda', serif";
const MONO = "'Geist Mono', monospace";

const CTR_STATS = [
  { label: "Jobs This Month", value: 4, Icon: Briefcase },
  { label: "Hours Logged", value: 76, Icon: Clock },
  { label: "Total Earnings", value: "$6,120", Icon: DollarSign, emphasis: true },
  { label: "Contracts Completed", value: 9, Icon: CheckCircle2 },
];

const CTR_EARNINGS = [
  ["Feb", 42], ["Mar", 66], ["Apr", 54], ["May", 82], ["Jun", 73], ["Jul", 95],
];

const CTR_PAYOUTS = [
  ["Northwind Logistics", "Jul 22", "$1,240"],
  ["Bright Harbor Care", "Jul 15", "$980"],
  ["Cardinal Events Co.", "Jul 09", "$1,560"],
];

export default function ContractorDashboardPreview() {
  return (
    <div className="min-h-screen text-ink" style={{ backgroundColor: "var(--bg)", fontFamily: "'Instrument Sans', sans-serif" }}>
      <AppNavbar />

      {/* Mobile top bar (matches DashboardLayout) */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-line bg-surface">
        <span className="p-2 rounded-lg text-ink"><Menu /></span>
        <div className="font-semibold text-ink">Dashboard</div>
        <div className="w-10" />
      </div>

      <div className="max-w-[1480px] mx-auto flex gap-8 px-4 sm:px-6 lg:px-10 py-6">
        <aside className="hidden lg:block w-[248px] shrink-0 sticky top-24 self-start">
          <Sidebar />
        </aside>
        <main className="flex-1 min-w-0">
          {/* Greeting + quick actions */}
          <div className="mb-7">
            <div style={{ fontFamily: MONO }} className="uppercase text-[0.625rem] tracking-[.24em] text-tertiary mb-3">
              Welcome back
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-pill border border-line-strong bg-surface px-4 py-2.5 text-sm font-semibold text-ink">
                <Clock className="h-4 w-4" /> Log time
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-pill bg-brand text-white px-4 py-2.5 text-sm font-semibold">
                Find work <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {CTR_STATS.map(({ label, value, Icon, emphasis }) => (
              <div key={label}
                className="rounded-2xl border p-5 flex flex-col gap-6"
                style={emphasis
                  ? { background: "#1B1813", borderColor: "#1B1813", color: "var(--on-dark,#FAF8F2)" }
                  : { background: "var(--surface)", borderColor: "var(--border)" }}>
                <span className="grid place-items-center rounded-xl" style={{ width: 42, height: 42, background: emphasis ? "rgba(201,161,76,.18)" : "var(--brand-tint)" }}>
                  <Icon className="h-5 w-5" strokeWidth={1.8} style={{ color: emphasis ? "#C9A14C" : "var(--brand)" }} />
                </span>
                <div>
                  <div style={{ fontFamily: MONO }} className="uppercase text-[0.625rem] tracking-[.16em]">
                    <span style={{ color: emphasis ? "rgba(250,248,242,.6)" : "var(--text-muted)" }}>{label}</span>
                  </div>
                  <div style={{ fontFamily: SERIF, fontSize: "2.5rem", lineHeight: 1.05, fontWeight: 600, marginTop: 4, color: emphasis ? "var(--on-dark,#FAF8F2)" : "var(--ink)" }}>
                    {value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Lower grid: earnings chart + payouts */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 items-start">
            <div className="rounded-2xl border border-line bg-surface p-5 shadow-sift-sm">
              <div style={{ fontFamily: MONO }} className="uppercase text-[0.625rem] tracking-[.16em] text-tertiary mb-1">Earnings</div>
              <div className="text-ink font-semibold text-lg mb-5" style={{ fontFamily: SERIF }}>Last 6 months</div>
              <div className="flex items-end gap-4" style={{ height: 168 }}>
                {CTR_EARNINGS.map(([m, h], i) => (
                  <div key={m} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                    <div className="w-full rounded-t-md" style={{ height: `${h}%`, background: i === CTR_EARNINGS.length - 1 ? "var(--brand)" : "var(--brand-tint)", border: "1px solid var(--brand)", borderBottom: "none" }} />
                    <span className="text-[0.625rem] text-tertiary">{m}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-5 shadow-sift-sm">
              <div style={{ fontFamily: MONO }} className="uppercase text-[0.625rem] tracking-[.16em] text-tertiary mb-3">Recent payouts</div>
              <ul className="divide-y divide-line">
                {CTR_PAYOUTS.map(([who, when, amt]) => (
                  <li key={who} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{who}</div>
                      <div className="text-xs text-tertiary">{when}</div>
                    </div>
                    <span className="text-sm font-semibold" style={{ color: "#1B8A5A", fontFamily: MONO }}>{amt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
